import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import LineChartCompare from '../gui/lineChartCompare';

// Up to 5 shooters can be compared; distinct hues keep the lines and cards readable.
const COLORS = [
    'var(--bs-primary)',
    'var(--bs-danger)',
    'var(--bs-success)',
    'var(--bs-orange)',
    'var(--bs-purple)',
];

@observer
class ShooterCompare extends Component {
    render() {
        const { shooters, shooterA, shooterB } = this.props;
        const { conventionStore, conventionResultStore } = this.props.stores;
        const { convention, participants, classMap, disciplineMap } = conventionStore;
        const { conventionResults } = conventionResultStore;

        // Accept the new `shooters` array; fall back to the legacy A/B pair for safety.
        const requested = (Array.isArray(shooters) && shooters.length
            ? shooters
            : [shooterA, shooterB])
            .filter(Boolean)
            .slice(0, COLORS.length);

        const details = requested
            .map(shooter => conventionResultStore.getShooterDetail(
                convention, conventionResults || [], participants || [],
                shooter, { classMap, disciplineMap },
            ))
            .filter(Boolean);

        const hasData = details.some(d => d.perStand.length > 0);
        if (details.length === 0 || !hasData) {
            return (
                <div class='p-3'>
                    <Text id='convention.compare-no-data'>No results to compare.</Text>
                </div>
            );
        }

        // Span the x-axis to the furthest stand actually shot, not just expectedStands.
        // Some felt stages under-report expectedStands (it falls back to stageStands.length),
        // which would otherwise truncate the chart to a couple of stands.
        const maxStand = details.reduce((m, d) => {
            const lastStand = d.perStand.reduce((mm, p) => Math.max(mm, p.stand), 0);
            return Math.max(m, d.expectedStands || 0, lastStand);
        }, 0);
        const xLabels = [];
        for (let i = 1; i <= maxStand; i += 1) xLabels.push(i);

        const series = details.map((d, i) => ({
            label: `${d.firstname} ${d.lastname}`,
            color: COLORS[i % COLORS.length],
            points: d.perStand.map(p => ({ x: p.stand, y: p.value })),
        }));

        return (
            <div class='p-3' style='padding-top: 48px;'>
                <div class='row g-3 mb-3'>
                    {details.map((d, i) => this.renderShooterCard(d, COLORS[i % COLORS.length], i))}
                </div>
                <LineChartCompare series={series} xLabels={xLabels} height={280} />
            </div>
        );
    }

    renderShooterCard(s, color, idx) {
        return (
            <div class='col-12 col-sm-6 col-lg-4' key={`shooter-${idx}`}>
                <div class='border rounded p-2 h-100' style={`border-left: 4px solid ${color} !important;`}>
                    <div class='fw-bold'>{s.firstname} {s.lastname}</div>
                    <div class='text-body-secondary small'>{s.sportsClub}</div>
                    <div class='small'>
                        {s.stageTypeDisplay} {s.weaponDisplay}
                        {s.skillClass && (<>
                            {' :: '}<Text id='convention.klasse'>Class</Text> {s.skillClass}
                        </>)}
                    </div>
                    <div class='d-flex gap-3 mt-2'>
                        <span><strong><Text id='convention.results-sum'>Sum</Text></strong> {s.totalScore}</span>
                        <span><strong>iX</strong> {s.totalIx}</span>
                    </div>
                </div>
            </div>
        );
    }
}

export default ShooterCompare;
